import {
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useState, ChangeEvent, useMemo } from "react";
import DialogLogin from "./partials/DialogLogin";
import { useAuthContext } from "../../../../../../hooks/useAuthContext";
import SelectCountries from "../../../../../../Components/Select/SelectCountries";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import PreviewMap from "./partials/Map";
import { ParentSize } from "@visx/responsive";
import useAreaOfInterest from "../../../../../../hooks/useAreaOfInterest";
import { FeatureCollection } from "geojson";
import FindInPageOutlinedIcon from "@mui/icons-material/FindInPageOutlined";
import { debounce } from "lodash";
import { useToast } from "../../../../../../Components/Provider/ToastProvider";
import Footer from "./partials/Footer";
import useQueryState from "../../../../../../hooks/useQueryState";
import FilePreview from "./partials/FilePreview";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

type AreaOfInterestAttributes = {
  country: string;
  file: File[];
  areaOfInterestName: string;
  idAttribute: string;
  nameAttribute: string;
};

const UploadShapeFile = () => {
  const { showToast } = useToast();
  const {
    queryState: { country },
  } = useQueryState();
  const { postAreaOfInterestPreview } = useAreaOfInterest();
  const [areaOfInterest, setAreaOfInterest] =
    useState<FeatureCollection | null>(null);
  const [localValue, setLocalValue] = useState("");
  const [existingAOI] = useState("");
  const { parsedToken } = useAuthContext();
  const [isOpen] = useState(true);
  const [isOpenFilePreview, setIsOpenFilePreview] = useState(false);
  const [attributes, setAttributes] = useState<AreaOfInterestAttributes>({
    country,
    file: [] as File[],
    areaOfInterestName: "",
    idAttribute: "",
    nameAttribute: "",
  });

  const handleRemoveFile = () => {
    setAttributes((prev) => ({
      ...prev,
      file: [],
      idAttribute: "",
      nameAttribute: "",
      areaOfInterestName: "",
    }));
  };

  const handleDownloadFile = () => {
    if (postAreaOfInterestPreview.data?.data) {
      const blob = new Blob(
        [JSON.stringify(postAreaOfInterestPreview.data?.data, null, 2)],
        {
          type: "application/geo+json",
        },
      );

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `ndgi-${attributes.file[0].name}`;
      link.click();

      URL.revokeObjectURL(url);
    }
  };

  const handleChangeFile = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    try {
      const response = await postAreaOfInterestPreview.mutateAsync({
        files: Array.from(event.target.files),
      });

      if (response) {
        setAreaOfInterest(response.data.geojson);
        setAttributes((prev) => ({
          ...prev,
          file: Array.from(event.target.files ?? []),
        }));
      }
    } catch (error) {
      showToast("error", "We couldn’t generate the preview. Please try again.");
      handleRemoveFile();
    } finally {
    }
  };

  const debouncedSetName = useMemo(
    () =>
      debounce((value: string) => {
        setAttributes((prev) => ({
          ...prev,
          areaOfInterestName: value,
        }));
      }, 500),
    [setAttributes],
  );

  return (
    <Stack height="100vh" width="100%" display="flex" flexDirection="column">
      <Stack sx={{ position: "relative" }} flex={1} alignItems="center">
        <Stack
          gap={5}
          sx={{
            filter: parsedToken?.email ? undefined : "blur(5px)",
            pointerEvents: parsedToken?.email ? "auto" : "none",
          }}
          maxWidth="720px"
        >
          <Stack gap={1}>
            <Typography variant="subtitle1" color="secondary">
              Target country
            </Typography>
            <SelectCountries label="" />
          </Stack>
          {attributes.file.length > 0 ? (
            <Stack gap={3}>
              <Stack gap={1}>
                <Typography variant="body1" fontWeight={700} color="#2D3648">
                  Shape file options
                </Typography>
                <Typography variant="body1" fontWeight={400} color="#515167">
                  Enter a name for the uploaded shapefile and select an
                  attribute file. The attribute file must contain unique values
                  for every polygon. You can verify the polygons buy hovering
                  the map or previewing the uploaded attribute file.
                </Typography>
              </Stack>
              <Stack direction="row" gap={2}>
                <Stack flex={2}>
                  <TextField
                    label="Name of shapefile"
                    placeholder="name"
                    value={localValue}
                    onChange={(e) => {
                      setLocalValue(e.target.value);
                      debouncedSetName(e.target.value);
                    }}
                  />
                </Stack>
                <Stack height="100%" flex={1}>
                  {attributes.file.map((item, index) => (
                    <Stack
                      direction="row"
                      key={index}
                      sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        height: "100%",
                        p: 1,
                        borderRadius: 1,
                        border: "1px solid #E8E8ED",
                        backgroundColor: "#FAFAFA",
                      }}
                      gap={0.5}
                    >
                      <Typography variant="body2" color="#1B1B22">
                        {item.name.length > 33
                          ? `${item.name.slice(0, 30)}...`
                          : item.name}
                      </Typography>

                      <Stack direction="row">
                        <IconButton
                          sx={{ padding: 0.5 }}
                          onClick={handleDownloadFile}
                        >
                          <FileDownloadIcon />
                        </IconButton>
                        <IconButton
                          sx={{ padding: 0.5 }}
                          onClick={handleRemoveFile}
                        >
                          <DeleteIcon sx={{ color: "#DA345B" }} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
              <Stack direction="row" gap={2}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Select attributes</InputLabel>
                  <Select
                    value={attributes.idAttribute}
                    label={`Select ID attribute`}
                    labelId={`Select ID attribute`}
                    onChange={(e) =>
                      setAttributes((prev) => ({
                        ...prev,
                        idAttribute: e.target.value,
                      }))
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 150,
                        },
                      },
                    }}
                  >
                    {!postAreaOfInterestPreview.data ? (
                      <MenuItem>
                        <Typography variant="caption" color="textSecondary">
                          There is no data to display
                        </Typography>
                      </MenuItem>
                    ) : (
                      postAreaOfInterestPreview.data?.data.attributes.map(
                        (attribute) => (
                          <MenuItem key={attribute} value={attribute}>
                            {attribute}
                          </MenuItem>
                        ),
                      )
                    )}
                  </Select>
                </FormControl>
                {/* <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Select Name attribute</InputLabel>
                  <Select
                    value={attributes.nameAttribute}
                    label={`Select name attribute`}
                    labelId={`Select name attribute`}
                    onChange={(e) =>
                      setAttributes({
                        ...attributes,
                        nameAttribute: e.target.value,
                      })
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 150,
                        },
                      },
                    }}
                  >
                    {!postAreaOfInterestPreview.data ? (
                      <MenuItem>
                        <Typography variant="caption" color="textSecondary">
                          There is no data to display
                        </Typography>
                      </MenuItem>
                    ) : (
                      postAreaOfInterestPreview.data?.data.attributes.map(
                        (attribute) => (
                          <MenuItem key={attribute} value={attribute}>
                            {attribute}
                          </MenuItem>
                        )
                      )
                    )}
                  </Select>
                </FormControl> */}
                <Button
                  variant="text"
                  onClick={() => setIsOpenFilePreview(true)}
                >
                  <Stack direction="row" gap={1} alignItems="center">
                    <FindInPageOutlinedIcon sx={{ color: "#2146A4" }} />
                    <Typography variant="body1" fontWeight={600}>
                      Show attribute file preview
                    </Typography>
                  </Stack>
                </Button>
              </Stack>
              <Stack width="100%" pb={10}>
                <ParentSize
                  parentSizeStyles={{ width: "100%", height: "100%" }}
                  key={attributes.file.length}
                >
                  {(parent) => (
                    <PreviewMap
                      onViewportChange={() => {}}
                      height="380px"
                      width="100%"
                      files={attributes.file}
                      areaOfInterest={areaOfInterest}
                    />
                  )}
                </ParentSize>
              </Stack>
            </Stack>
          ) : (
            <Stack gap={5}>
              {/* <Stack gap={2}>
                <Typography variant="subtitle1" color="secondary">
                  Select a shapefile from your shape library.
                </Typography>
                <FormControl fullWidth>
                  <InputLabel id="select-shapefile">
                    Select shapefile
                  </InputLabel>

                  <Select
                    labelId="select-shapefile"
                    value={existingAOI}
                    label="Select shapefile"
                    displayEmpty
                    disabled={
                      attributes.file.length > 0 &&
                      postAreaOfInterestPreview.isLoading
                    }
                    onChange={(e) => setExistingAOI(e.target.value)}
                    renderValue={(selected) => {
                      if (!selected) return "";
                      const aoi = getAreaOfInterests?.data?.data.find(
                        (x) => String(x.id) === selected
                      );
                      return aoi?.areaOfInterestName ?? "";
                    }}
                    endAdornment={
                      existingAOI &&
                      attributes.file.length === 0 && (
                        <IconButton
                          size="small"
                          sx={{ mr: 1 }}
                          onClick={(e) => {
                            e.stopPropagation(); // 👈 prevents menu from opening
                            setExistingAOI("");
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    {getAreaOfInterests.isLoading ? (
                      <MenuItem
                        value=""
                        disabled
                        sx={{ justifyContent: "center" }}
                      >
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : getAreaOfInterests?.data?.data.length ? (
                      getAreaOfInterests.data.data.map((aoi) => (
                        <MenuItem key={aoi.id} value={String(aoi.id)}>
                          {aoi.areaOfInterestName}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>
                        There is no data to display
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Stack> */}
              {/* <Stack direction="row" alignItems="center">
                <Box flex={1}>
                  <Divider />
                </Box>
                <Typography mx={5} color="secondary" variant="subtitle1">
                  Or
                </Typography>
                <Box flex={1}>
                  <Divider />
                </Box>
              </Stack> */}
              <Stack gap={1} mb={5}>
                <Typography variant="subtitle1" color="secondary">
                  Upload your geographic data as either a .zip with all
                  shapefile components (.shp, .shx, .dbf, optionally .prj), or a
                  single .geojson file. Include one attribute with unique values
                  to name each geographic unit in the dashboard. Geographic
                  extent must not exceed a single country (sub-national units
                  only).
                </Typography>
                <Stack
                  padding="1rem"
                  direction="column"
                  justifyContent={
                    postAreaOfInterestPreview.isLoading
                      ? "center"
                      : "space-between"
                  }
                  alignItems="center"
                  tabIndex={-1}
                  sx={{
                    height: "180px",
                    width: "100%",
                    borderRadius: "8px",
                    border: `1px dashed ${postAreaOfInterestPreview.isError ? "red" : "#2146A4"}`,
                    background: "#FAFAFA",
                    boxShadow: "none",
                  }}
                >
                  {postAreaOfInterestPreview.isLoading ? (
                    <CircularProgress />
                  ) : (
                    <>
                      <UploadFileIcon
                        sx={{
                          height: "48px",
                          width: "auto",
                          color: "dhiGrey.500",
                        }}
                      />
                      <Typography color="secondary" fontWeight="700">
                        Drag to upload shapefile
                      </Typography>
                      <Typography color="secondary">Or</Typography>
                      <Button
                        component="label"
                        role={undefined}
                        variant="outlined"
                        tabIndex={-1}
                        startIcon={<UploadFileIcon />}
                        disabled={!!existingAOI}
                        sx={{
                          borderColor: "ngdi.primary",
                          color: "ngdi.primary",
                          textTransform: "none",
                          fontWeight: 500,
                        }}
                      >
                        Upload shapefile
                        <VisuallyHiddenInput
                          type="file"
                          accept=".zip,.geojson"
                          onChange={handleChangeFile}
                        />
                      </Button>
                    </>
                  )}
                </Stack>
                {postAreaOfInterestPreview.isError && (
                  <Typography fontSize="caption" color="red">
                    The uploaded file is not supported. Please upload a valid
                    file
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
        {isOpen && !parsedToken?.email && <DialogLogin />}
        {
          <FilePreview
            isOpen={isOpenFilePreview}
            setIsOpen={setIsOpenFilePreview}
            areaOfInterests={postAreaOfInterestPreview?.data?.data ?? null}
          />
        }
      </Stack>
      <Footer attributes={{ ...attributes, country }} disabled={isOpen} />
    </Stack>
  );
};

export default UploadShapeFile;
